// src/App.js
// Trigger build: Testing CI/CD pipeline
// Updated AWS role configuration for all repositories
// Updated ECS service names for all repositories
// Fixed cluster names: QA uses dev naming convention
// Fixed service names: Dev without "1", QA with "1"
// Trigger: Testing corrected ECS service names in workflow
// Fixed Step Function: QA uses Conductor-v2-dev-workflow
//Updated AWS role configuration for all repositories
//Updated ECS service names for all repositories
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import theme from './theme';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { harmonateTokens as ht } from './theme/harmonateTokens';

// Layout components
import Sidebar from './components/common/Sidebar';
import Dashboard from './components/Menu/Dashboard';
import Workflows from './components/Menu/Workflows/Workflows';
import Treasury from './components/Menu/Treasury';
import ApHub from './components/Menu/ApHub';
import WireTransferViewerPage from './components/Menu/Treasury/WireTransferViewerPage';
import TreasuryPaymentViewerPage from './components/Menu/TreasuryPayment/TreasuryPaymentViewerPage';
import AnomalyDetection from './components/Menu/AnomalyDetection';
import GLSync from './components/Menu/GLSync';
import UploadScreen from './components/Menu/UploadScreen';
import SyncUploadScreen from './components/Menu/SyncUploadScreen';
import GLSyncMapping from './components/Menu/GLSyncMapping';
import SyncTrialBalanceCompare from './components/Menu/SyncTrialBalanceCompare';
import DebtCollection from './components/Menu/DebtCollection';
import AccountsPayableRecovery from './components/Menu/AccountsPayableRecovery';
import Profile from './components/pages/Profile';
import Settings from './components/pages/Settings';
import NotFound from './components/pages/NotFound';
import NoAccess from './components/pages/NoAccess';
import Login from './components/auth/Login';
import OAuthAuthorize from './components/auth/OAuthAuthorize';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import DocumentManagementPage from './components/pages/DocumentManagementPage';
import Document2 from './components/pages/Document2';
import Document3 from './components/pages/Document3';
import Documents from './components/pages/Documents';
import DocumentTypeFiles from './components/Menu/DocumentTypeFiles';
import Workflows1 from './components/pages/Workflows1';
import CollectionTierInfo from './components/Menu/CollectionTierInfo';
import Initiation from './components/Menu/Initiation';
import Test from './components/Menu/Test';
import Tenant from './components/Menu/Tenant/Tenant';
import TenantRole from './components/Menu/TenantRole/TenantRole';
import User from './components/Menu/User/User';
import GlobalUser from './components/Menu/GlobalUser/GlobalUser';
import WorkflowDefinition from './components/Menu/WorkflowDefinition/WorkflowDefinition';
import MasterData from './components/Menu/MasterData/MasterData';
import CashRecon from './components/Menu/CashRecon/CashRecon';
import Mapping from './components/Menu/CashRecon/Mapping';
import Compare from './components/Menu/CashRecon/Compare';
import Output from './components/Menu/CashRecon/Output';
import Chargeback from './components/Menu/Chargeback/Chargeback';
import DocumentTypeView from './components/Menu/DocumentTypeView/DocumentTypeView';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider } from './context/AuthContext';
import PermissionProtectedRoute, { AuthAndPermissionRoute } from './components/auth/PermissionProtectedRoute';
import { FC } from './constants/functionCodes';
import { UserRightsProvider } from './context/UserRightsContext';
import GLSyncGLMappingWorkflow from './components/Menu/GLSyncGLMappingWorkflow';
import GLMappingResults from './components/Menu/GLMappingResults';
import GLSyncMappingEdit from './components/Menu/GLSyncMappingEdit';
import GLSyncMappingViewOnly from './components/Menu/GLSyncMappingViewOnly';
import AppConfig from './components/Menu/AppConfig/AppConfig';
import NotificationConfiguration from './components/Menu/NotificationConfiguration/NotificationConfiguration';
import NotificationCenter from './components/Menu/NotificationCenter/NotificationCenter';
import ExternalIntegrationHub from './components/Menu/ExternalIntegration/ExternalIntegrationHub';
import UserLogs from './components/Menu/UserLogs';
import ReferenceTemplates from './components/Menu/ReferenceTemplates';
import MerchantDataHubDashboard from './components/Menu/CreditCard/MerchantDataHubDashboard';
import CreditCardTransactions from './components/Menu/CreditCard/CreditCardTransactions';
import GLAccounts from './components/Menu/GLAccounts';
import TenantWorkflow from './components/Menu/TenantWorkflow/TenantWorkflow';
import Charge from './components/Menu/Chargeback/Charge';
import CaseDetails from './components/Menu/Chargeback/CaseDetails';
import DisputeLetter from './components/Menu/Chargeback/DisputeLetter';
import WorkforceOperations from './components/Menu/WorkforceOperations/WorkforceOperations';
import PmsTransactionGLMapping from './components/Menu/HotelSync/PmsTransactionGLMapping';
import Property from './components/Menu/Property/Property';
import PropertyGroup from './components/Menu/Property/PropertyGroup';
import VendorMaster from './components/Menu/VendorMaster/VendorMaster';
import BusinessUnitMaster from './components/Menu/BusinessUnitMaster/BusinessUnitMaster';
import GlAccountMaster from './components/Menu/GlAccountMaster/GlAccountMaster';
import BankMaster from './components/Menu/BankMaster/BankMaster';
import BudgetCodeMaster from './components/Menu/BudgetCodeMaster/BudgetCodeMaster';
import DepartmentMaster from './components/Menu/DepartmentMaster/DepartmentMaster';
import ContractMaster from './components/Menu/ContractMaster/ContractMaster';
import ApWorkflowSetup from './components/Menu/ApWorkflowSetup/ApWorkflowSetup';
import WorkforceOtState from './components/Menu/WorkforceOtState/WorkforceOtState';
import WorkforcePositionMaster from './components/Menu/WorkforcePositionMaster/WorkforcePositionMaster';
import GlobalEmployee from './components/Menu/GlobalEmployee/GlobalEmployee';
import WorkforceSkillCertificateMaster from './components/Menu/WorkforceSkillCertificateMaster/WorkforceSkillCertificateMaster';
import { isHarmonateUser as isServerHarmonateUser } from './auth/serverIdentity';

const PropertyConfigurationPage = () => <Property />;
const PropertyGroupConfigurationPage = () => <PropertyGroup />;

// Inside your router configuration
<Route path="/gl-accounts" element={<GLAccounts />} />

// Main Layout component
const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - 64px)` },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            p: 0,
            bgcolor: 'transparent',
            background: ht.pageBg,
            color: ht.white,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

// Enhanced Protected Route component with permission checking
const ProtectedRoute = ({ 
  children, 
  functionCode, 
  requiredPermission = 'view'
}) => {
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const isHarmonateUser = isServerHarmonateUser();
  
  // Check if user is logged in
  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // For non-harmonate users, show all routes (original behavior)
  if (!isHarmonateUser) {
    return children;
  }

  // For harmonate users with specific permission requirements
  if (functionCode && requiredPermission) {
    // This will be handled by the UserRightsProvider context
    // The actual permission check will happen in the component
    return children;
  }

  return children;
};

// Permission-aware route wrapper
const PermissionRoute = ({ 
  element, 
  functionCode, 
  requiredPermission = 'view' 
}) => {
  return (
    <AuthAndPermissionRoute
      functionCode={functionCode}
      requiredPermission={requiredPermission}
    >
      {element}
    </AuthAndPermissionRoute>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    const handleLogoutSuccess = () => {
      toast.success('Logout successful.');
    };
    
    const handleLoginSuccess = () => {
      const isHarmonateUser = isServerHarmonateUser();
      if (isHarmonateUser) {
        toast.info('Welcome! You have full administrative access.');
      } else {
        toast.info('Welcome! Your access is based on assigned permissions.');
      }
    };

    window.addEventListener('logout-success', handleLogoutSuccess);
    window.addEventListener('login-success', handleLoginSuccess);
    
    return () => {
      window.removeEventListener('logout-success', handleLogoutSuccess);
      window.removeEventListener('login-success', handleLoginSuccess);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <NotificationProvider>
            <UserRightsProvider>
              <CssBaseline />
              <BrowserRouter>
                <Routes>
                  {/* Root redirect to login */}
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  
                  {/* Login route */}
                  <Route path="/login" element={<Login />} />

                  {/* OAuth consent (Sync reverse connector — public, no app shell) */}
                  <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
                  
                  {/* Public routes */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route path="/document-type/:type" element={<DocumentTypeFiles />} />
                  
                  {/* Main app routes with layout */}
                  <Route 
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route path="/dashboard" element={
                      <AuthAndPermissionRoute functionCode={FC.HOME} requiredPermission="enable">
                        <Dashboard />
                      </AuthAndPermissionRoute>
                    } />
                    <Route path="/no-access" element={<NoAccess />} />
                    <Route path="/workforce-operations" element={
                      <AuthAndPermissionRoute functionCode={FC.WORKFORCE_OPTIMIZATION} requiredPermission="view">
                        <WorkforceOperations />
                      </AuthAndPermissionRoute>
                    } />
                    <Route path="/hotel-sync" element={<Navigate to="/hotel-sync/pms-gl-mapping" replace />} />
                    <Route path="/hotel-sync/pms-gl-mapping" element={
                      <AuthAndPermissionRoute functionCode={FC.PMS_RECONCILIATION} requiredPermission="view">
                        <PmsTransactionGLMapping />
                      </AuthAndPermissionRoute>
                    } />
                    
                    {/* Merchant Data Hub — landing + Credit Card Reports */}
                    <Route path="/credit-card" element={
                      <AuthAndPermissionRoute
                        functionCode={FC.MERCHANT_DATA_HUB}
                        requiredPermission="view"
                      >
                        <MerchantDataHubDashboard />
                      </AuthAndPermissionRoute>
                    } />
                    <Route path="/credit-card/transactions" element={
                      <AuthAndPermissionRoute functionCode={FC.MERCHANT_DATA_HUB} requiredPermission="view">
                        <CreditCardTransactions />
                      </AuthAndPermissionRoute>
                    } />
                    
                    {/* FundIQ product split — Document Validation (Internal) vs ETVL (External) */}
                    <Route
  path="/workflows/document-validation"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.DOCUMENT_VALIDATION}
      requiredPermission="view"
    >
      <Workflows productScope="internal" />
    </AuthAndPermissionRoute>
  }
/>
                    <Route
  path="/workflows/etvl"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.ETVL_ENGINE}
      requiredPermission="view"
    >
      <Workflows productScope="external" />
    </AuthAndPermissionRoute>
  }
/>
                    {/* Legacy unfiltered FundIQ list (bookmarks / deep links). Chargeback uses /chargeback/workflows. */}
                    <Route
  path="/workflows"
  element={
    <AuthAndPermissionRoute
      functionCodes={[FC.DOCUMENT_VALIDATION, FC.ETVL_ENGINE]}
      requiredPermission="view"
    >
      <Workflows />
    </AuthAndPermissionRoute>
  }
/>
                    <Route path="/workflows1" element={<Workflows1 />} />
                    
                    {/* Feature Routes with permission checks */}
                    <Route 
  path="/anomaly-detection" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.ANOMALY_DETECTION}
      requiredPermission="view"
    >
      <AnomalyDetection />
    </AuthAndPermissionRoute>
  } 
/>
                    
                    {/* Configuration Routes with permission checks */}
                    <Route 
  path="/tenant" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.TENANT}
      requiredPermission="view"
    >
      <Tenant />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/workflow-definition" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.WORKFLOW_DEFINITION}
      requiredPermission="view"
    >
      <WorkflowDefinition />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/tenant-role" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.ROLE}
      requiredPermission="view"
    >
      <TenantRole />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/master-data" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MASTER_DATA}
      requiredPermission="view"
    >
      <MasterData />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/configuration/property" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PROPERTY}
      requiredPermission="view"
    >
      <PropertyConfigurationPage />
    </AuthAndPermissionRoute>
  } 
/>

<Route
  path="/configuration/property-group"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PROPERTY_GROUP}
      requiredPermission="view"
    >
      <PropertyGroupConfigurationPage />
    </AuthAndPermissionRoute>
  }
/>

<Route
  path="/configuration/workforce-ot-state"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.WORKFORCE_OT_STATE}
      requiredPermission="view"
    >
      <WorkforceOtState />
    </AuthAndPermissionRoute>
  }
/>

<Route
  path="/configuration/position-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.POSITION_MASTER}
      requiredPermission="view"
    >
      <WorkforcePositionMaster />
    </AuthAndPermissionRoute>
  }
/>

<Route
  path="/configuration/skills-certifications"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.SKILLS_CERTIFICATIONS}
      requiredPermission="view"
    >
      <WorkforceSkillCertificateMaster />
    </AuthAndPermissionRoute>
  }
/>

<Route
  path="/configuration/ap-setup/*"
  element={<Navigate to="/configuration/vendor-master" replace />}
/>

<Route
  path="/configuration/vendor-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.VENDOR_MASTER}
      requiredPermission="view"
    >
      <VendorMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/business-unit-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.ACCOUNTS_PAYABLE}
      requiredPermission="view"
    >
      <BusinessUnitMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/gl-account-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_ACCOUNT_MASTER}
      requiredPermission="view"
    >
      <GlAccountMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/bank-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.BANK_MASTER}
      requiredPermission="view"
    >
      <BankMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/budget-codes"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.BUDGET_REVENUE_CODES}
      requiredPermission="view"
    >
      <BudgetCodeMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/department-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.DEPARTMENT_MASTER}
      requiredPermission="view"
    >
      <DepartmentMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/global-employee"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GLOBAL_EMPLOYEE}
      requiredPermission="view"
    >
      <GlobalEmployee />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/contract-master"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.CONTRACT_MASTER}
      requiredPermission="view"
    >
      <ContractMaster />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/configuration/ap-workflow-setup"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.WORKFLOW}
      requiredPermission="view"
    >
      <ApWorkflowSetup />
    </AuthAndPermissionRoute>
  }
/>

<Route 
  path="/document-type-view" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.DOCUMENT_TYPE}
      requiredPermission="view"
    >
      <DocumentTypeView />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/app-config" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.APP_CONFIG}
      requiredPermission="view"
    >
      <AppConfig />
    </AuthAndPermissionRoute>
  } 
/>

<Route
  path="/configuration/notification-rules"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.NOTIFICATION_CONFIGURATION}
      requiredPermission="view"
    >
      <NotificationConfiguration />
    </AuthAndPermissionRoute>
  }
/>

<Route
  path="/notifications"
  element={<NotificationCenter />}
/>

<Route
  path="/configuration/external-integration"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.EXTERNAL_INTEGRATION}
      requiredPermission="view"
    >
      <ExternalIntegrationHub />
    </AuthAndPermissionRoute>
  }
/>

<Route 
  path="/user-logs" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.AUDIT_LOGS}
      requiredPermission="view"
    >
      <UserLogs mode="tenant" />
    </AuthAndPermissionRoute>
  } 
/>

<Route
  path="/harmonate/audit-logs"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.AUDIT_LOGS}
      requiredPermission="view"
    >
      <UserLogs mode="application" />
    </AuthAndPermissionRoute>
  }
/>

<Route 
  path="/reference-templates" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.REFERENCE_TEMPLATES}
      requiredPermission="view"
    >
      <ReferenceTemplates />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/user" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.USERS}
      requiredPermission="view"
    >
      <User />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/global-user" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GLOBAL_USERS}
      requiredPermission="view"
    >
      <GlobalUser />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/cash-recon" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.CASH_RECONCILIATION}
      requiredPermission="view"
    >
      <Mapping />
    </AuthAndPermissionRoute>
  } 
/>

{/* Keep mapping route for backward compatibility - redirects to main cash-recon */}
<Route 
  path="/cash-recon/mapping" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.CASH_RECONCILIATION}
      requiredPermission="view"
    >
      <Mapping />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/cash-recon/compare" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.CASH_RECONCILIATION}
      requiredPermission="view"
    >
      <Compare />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/cash-recon/output" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.CASH_RECONCILIATION}
      requiredPermission="view"
    >
      <Output />
    </AuthAndPermissionRoute>
  } 
/>

<Route
  path="/chargeback"
  element={<Navigate to="/chargeback/charge/default-charge" replace />}
/>

<Route
  path="/chargeback/insights"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MERCHANT_DATA_HUB}
      requiredPermission="view"
    >
      <Chargeback />
    </AuthAndPermissionRoute>
  }
/>

<Route 
  path="/chargeback/charge/:chargeId" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MERCHANT_DATA_HUB}
      requiredPermission="view"
    >
      <Charge />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/chargeback/dispute-letter" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MERCHANT_DATA_HUB}
      requiredPermission="view"
    >
      <DisputeLetter />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/chargeback/workflows" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MERCHANT_DATA_HUB}
      requiredPermission="view"
    >
      <Workflows />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/chargeback/case/:caseId" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.MERCHANT_DATA_HUB}
      requiredPermission="view"
    >
      <CaseDetails />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/gl-sync" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="view"
    >
      <GLSync />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/treasury" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.TREASURY}
      requiredPermission="view"
    >
      <Treasury />
    </AuthAndPermissionRoute>
  } 
/>
<Route
  path="/treasury-payment/workflows"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/invoices"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/invoice-uploads"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <Navigate to="/treasury-payment/invoices?tab=draft" replace />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/payment-batches"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/gl-assignments"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/budget-vs-pi"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route
  path="/treasury-payment/payment-proposal"
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  }
/>
<Route 
  path="/treasury-payment" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <ApHub />
    </AuthAndPermissionRoute>
  } 
/>
<Route 
  path="/treasury-payment/:fileId" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.PAYMENT_INTELLIGENCE}
      requiredPermission="view"
    >
      <TreasuryPaymentViewerPage />
    </AuthAndPermissionRoute>
  } 
/>
<Route 
  path="/treasury/wire-transfer/:processId" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.TREASURY}
      requiredPermission="view"
    >
      <WireTransferViewerPage />
    </AuthAndPermissionRoute>
  } 
/>
                    
<Route 
  path="/gl-sync/upload" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="upload"
    >
      <UploadScreen />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/gl-sync/sync-upload" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="upload"
    >
      <SyncUploadScreen />
    </AuthAndPermissionRoute>
  } 
/>
                    
<Route 
  path="/gl-sync/mapping" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="edit"
    >
      <GLSyncMapping />
    </AuthAndPermissionRoute>
  } 
/>
<Route 
  // path="/gl-sync/edit/:cardId" 
  path="/gl-sync/edit" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="edit"
    >
      <GLSyncMappingEdit />
    </AuthAndPermissionRoute>
  } 
/>

<Route 
  path="/gl-sync/view-only" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.GL_SYNC}
      requiredPermission="view"
    >
      <GLSyncMappingViewOnly />
    </AuthAndPermissionRoute>
  } 
/>
<Route path="/gl-accounts" 
element={
  <AuthAndPermissionRoute
    functionCode={FC.GL_SYNC}
    requiredPermission="view"
  >
    <GLAccounts />
  </AuthAndPermissionRoute>
}  />
<Route path="/tenant-workflow" 
element={
  <AuthAndPermissionRoute
    functionCode={FC.TENANT_WORKFLOW}
    requiredPermission="view"
  >
    <TenantWorkflow />
  </AuthAndPermissionRoute>
}  />


                    <Route 
                      path="/gl-sync/upload-mapping" 
                      element={
                        <PermissionRoute
                          element={<GLSyncGLMappingWorkflow />}
                          functionCode={FC.GL_SYNC}
                          requiredPermission="edit"
                        />
                      } 
                    />

                    <Route 
                      path="/gl-sync/gl-mapping-results" 
                      element={
                        <PermissionRoute
                          element={<GLMappingResults />}
                          functionCode={FC.GL_SYNC}
                          requiredPermission="view"
                        />
                      } 
                    />
                    
                    <Route 
                      path="/gl-sync/trial-balance-compare" 
                      element={
                        <PermissionRoute
                          element={<SyncTrialBalanceCompare />}
                          functionCode={FC.GL_SYNC}
                          requiredPermission="view"
                        />
                      } 
                    />
                    
                    {/* Other Feature Routes */}
                    <Route 
  path="/debt-collection" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.DEBT_COLLECTION}
      requiredPermission="view"
    >
      <DebtCollection />
    </AuthAndPermissionRoute>
  } 
/>
                    
                    <Route path="/collection-tier-info" element={<CollectionTierInfo />} />
                    <Route path="/initiation" element={<Initiation />} />
                    
                    <Route 
  path="/accounts-payable-recovery" 
  element={
    <AuthAndPermissionRoute
      functionCode={FC.ACCOUNTS_PAYABLE}
      requiredPermission="view"
    >
      <AccountsPayableRecovery />
    </AuthAndPermissionRoute>
  } 
/>

                    <Route path="/invoices" element={<Navigate to="/treasury-payment/invoices" replace />} />
                    <Route path="/payment-proposal" element={<Navigate to="/treasury-payment/payment-proposal" replace />} />
                    
                    {/* Always accessible routes */}
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    
                    {/* Document routes */}
                    <Route path="/document-management-page" element={<DocumentManagementPage />} />
                    <Route path="/document2" element={<Document2 />} />
                    <Route path="/document3" element={<Document3 />} />
                    <Route path="/documents" element={
                      <AuthAndPermissionRoute functionCode={FC.DOCUMENTS} requiredPermission="view">
                        <Documents />
                      </AuthAndPermissionRoute>
                    } />
                    
                    {/* Test route */}
                    <Route path="/test" element={<Test />} />
                  </Route>
                  
                  {/* 404 - this should be last */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                
                {/* Toast Container */}
                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                />
              </BrowserRouter>
            </UserRightsProvider>
          </NotificationProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;